import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Box,
  Chip,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
} from '@mui/material';
import {
  CleaningServices,
  CheckCircle,
  Cancel,
  Home,
  Business,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { cleanerApi, Property as ApiProperty } from '../services/api';

interface Unit {
  id: number;
  unit_number: string;
  name: string;
  type: string;
  status: string;
  cleaning_status: 'cleaned' | 'not_cleaned';
}

interface CleaningSummary {
  property_id: number;
  property_name: string;
  total_units: number;
  cleaned_units: number;
  not_cleaned_units: number;
  completion_percentage: number;
}

const CleanerPage: React.FC = () => {
  const { user } = useAuth();
  const [properties, setProperties] = useState<ApiProperty[]>([]);
  const [summary, setSummary] = useState<CleaningSummary[]>([]);
  const [selectedProperty, setSelectedProperty] = useState<ApiProperty | null>(null);
  const [units, setUnits] = useState<Unit[]>([]);
  const [loading, setLoading] = useState(true);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [updating, setUpdating] = useState<number | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchAssignedProperties();
    fetchCleaningSummary();
  }, []);

  const fetchAssignedProperties = async () => {
    try {
      setLoading(true);
      const properties = await cleanerApi.getAssignedProperties();
      setProperties(properties);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to fetch assigned properties');
    } finally {
      setLoading(false);
    }
  };

  const fetchCleaningSummary = async () => {
    try {
      const summary = await cleanerApi.getCleaningSummary();
      setSummary(summary);
    } catch (error: any) {
      console.error('Failed to fetch cleaning summary:', error);
    }
  };

  const fetchPropertyUnits = async (propertyId: number) => {
    try {
      setUnitsLoading(true);
      const units = await cleanerApi.getPropertyUnits(propertyId);
      setUnits(units);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to fetch units');
    } finally {
      setUnitsLoading(false);
    }
  };

  const updateUnitCleaningStatus = async (unitId: number, status: 'cleaned' | 'not_cleaned') => {
    try {
      setUpdating(unitId);
      await cleanerApi.updateUnitCleaningStatus(unitId, status);

      // Update local state
      setUnits(prevUnits =>
        prevUnits.map(unit =>
          unit.id === unitId ? { ...unit, cleaning_status: status } : unit
        )
      );

      // Refresh summary
      fetchCleaningSummary();
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to update cleaning status');
    } finally {
      setUpdating(null);
    }
  };

  const handlePropertySelect = (property: ApiProperty) => {
    setSelectedProperty(property);
    setOpenDialog(true);
    fetchPropertyUnits(property.id);
  };

  const getCleaningStatusColor = (status: string) => {
    return status === 'cleaned' ? 'success' : 'warning';
  };

  const getPropertySummary = (propertyId: number) => {
    return summary.find(s => s.property_id === propertyId);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CleaningServices color="primary" />
          Cleaner Dashboard
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Welcome back, {user?.name}! Manage your assigned property cleanings here.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {properties.length === 0 ? (
        <Alert severity="info">
          No properties assigned to you yet. Please contact your administrator.
        </Alert>
      ) : (
        <>
          <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
            Assigned Properties ({properties.length})
          </Typography>

          <Grid container spacing={3}>
            {properties.map((property) => {
              const propertySummary = getPropertySummary(property.id);
              return (
                <Grid item xs={12} md={6} lg={4} key={property.id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: 'pointer',
                      '&:hover': {
                        boxShadow: 6,
                      },
                    }}
                    onClick={() => handlePropertySelect(property)}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
                        {property.type === 'building' ? <Business /> : <Home />}
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="h6" component="h3" gutterBottom>
                            {property.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {property.address}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {property.city}, {property.country}
                          </Typography>
                        </Box>
                      </Box>

                      {propertySummary && (
                        <Box sx={{ mt: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2">
                              Cleaning Progress
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {propertySummary.completion_percentage}%
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={propertySummary.completion_percentage}
                            sx={{ mb: 2 }}
                          />
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Chip
                              icon={<CheckCircle />}
                              label={`${propertySummary.cleaned_units} Cleaned`}
                              color="success"
                              size="small"
                            />
                            <Chip
                              icon={<Cancel />}
                              label={`${propertySummary.not_cleaned_units} Pending`}
                              color="warning"
                              size="small"
                            />
                          </Box>
                        </Box>
                      )}
                    </CardContent>

                    <CardActions>
                      <Button
                        size="small"
                        color="primary"
                        startIcon={<CleaningServices />}
                      >
                        Manage Cleaning
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </>
      )}

      {/* Units Management Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedProperty?.name} - Units Cleaning Status
        </DialogTitle>
        <DialogContent>
          {unitsLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <List>
              {units.map((unit) => (
                <ListItem key={unit.id} divider>
                  <ListItemText
                    primary={`${unit.unit_number} - ${unit.name}`}
                    secondary={`Type: ${unit.type} | Status: ${unit.status}`}
                  />
                  <ListItemSecondaryAction>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={unit.cleaning_status === 'cleaned'}
                          onChange={(e) =>
                            updateUnitCleaningStatus(
                              unit.id,
                              e.target.checked ? 'cleaned' : 'not_cleaned'
                            )
                          }
                          disabled={updating === unit.id}
                          color="success"
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            size="small"
                            label={unit.cleaning_status === 'cleaned' ? 'Cleaned' : 'Not Cleaned'}
                            color={getCleaningStatusColor(unit.cleaning_status)}
                            icon={unit.cleaning_status === 'cleaned' ? <CheckCircle /> : <Cancel />}
                          />
                          {updating === unit.id && <CircularProgress size={16} />}
                        </Box>
                      }
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CleanerPage;
